import { Card, CardContent } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { cn } from "@/shared/lib/cn.js";

/**
 * Peças visuais repetidas nas telas do Master.
 *
 * Ficam aqui em vez de serem recopiadas em cada uma das dez telas.
 */

/**
 * Cartão de número.
 * @param {{ label: string, value: React.ReactNode, hint?: string,
 *           icon?: React.ElementType, accent?: string }} props
 */
export function StatCard({ label, value, hint, icon: Icon, accent = "primary" }) {
  return (
    <Card className="relative overflow-hidden">
      <div
        className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-10 blur-2xl"
        style={{ background: `var(--color-${accent})` }}
      />
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground">{label}</p>
            <p className="mt-2 font-display text-2xl font-semibold tracking-tight">{value}</p>
            {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
          </div>
          {Icon && (
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-${accent}/10 text-${accent}`}
            >
              <Icon className="h-5 w-5" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------- selos

const STATUS_CLIENTE = {
  ativo: { label: "Ativo", variant: "success" },
  demonstracao: { label: "Demonstração", variant: "secondary" },
  inadimplente: { label: "Inadimplente", variant: "destructive" },
  cancelado: { label: "Cancelado", variant: "outline" },
};

/** @param {{ status: string }} props */
export function ClientStatusBadge({ status }) {
  const meta = STATUS_CLIENTE[status] ?? { label: status, variant: "outline" };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

export const CLIENT_STATUS_OPTIONS = Object.entries(STATUS_CLIENTE).map(([value, m]) => ({
  value,
  label: m.label,
}));

const STATUS_FATURA = {
  paga: { label: "Paga", variant: "success" },
  pendente: { label: "Pendente", variant: "warning" },
  atrasada: { label: "Atrasada", variant: "destructive" },
};

/** @param {{ status: string }} props */
export function InvoiceStatusBadge({ status }) {
  const meta = STATUS_FATURA[status] ?? { label: status, variant: "outline" };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

export const INVOICE_STATUS_OPTIONS = Object.entries(STATUS_FATURA).map(([value, m]) => ({
  value,
  label: m.label,
}));

const STATUS_CHAMADO = {
  aberto: { label: "Aberto", variant: "warning" },
  "em-andamento": { label: "Em andamento", variant: "secondary" },
  resolvido: { label: "Resolvido", variant: "success" },
};

/** @param {{ status: string }} props */
export function TicketStatusBadge({ status }) {
  const meta = STATUS_CHAMADO[status] ?? { label: status, variant: "outline" };
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

export const TICKET_STATUS_OPTIONS = Object.entries(STATUS_CHAMADO).map(([value, m]) => ({
  value,
  label: m.label,
}));

const PRIORIDADES = {
  alta: "text-destructive",
  média: "text-warning",
  baixa: "text-muted-foreground",
};

/** @param {{ priority: string }} props */
export function PriorityLabel({ priority }) {
  return (
    <span className={cn("text-xs font-medium capitalize", PRIORIDADES[priority] ?? "")}>
      {priority}
    </span>
  );
}

/**
 * Barra de uso com aviso visual quando o limite do plano está próximo.
 * @param {{ value: number, limit: number, label?: string }} props
 */
export function UsageBar({ value, limit, label }) {
  const semLimite = !limit || limit <= 0;
  const percentual = semLimite ? 0 : Math.min(100, Math.round((value / limit) * 100));

  const cor =
    percentual >= 100 ? "bg-destructive" : percentual >= 80 ? "bg-warning" : "bg-primary";

  return (
    <div className="min-w-32">
      <div className="mb-1 flex items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {value}
          {!semLimite && <span className="text-muted-foreground"> / {limit}</span>}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all", cor)}
          style={{ width: `${semLimite ? 0 : percentual}%` }}
        />
      </div>
    </div>
  );
}
